import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CodeMonkey } from '../components/CodeMonkey';
import { Helmet } from 'react-helmet';
import { TrendingUp, Link2, Trophy, Zap, Code, Search, MessageCircle, Newspaper, Calendar, HelpCircle, ChevronRight } from 'lucide-react';

const Home = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: false }); // Optimize canvas
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 20; // Reduced for better performance

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1
      });
    }

    let animationFrameId;
    function animate() {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(201, 176, 55, 0.5)';
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* FAQ Schema for SEO */}
      <Helmet>
        <link rel="canonical" href="https://www.flumberico.fun/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is FLUMBERICO?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "FLUMBERICO is an ultra-fast trending SEO social experiment and meme coin on the Solana blockchain. It's the world's first tokenization of SEO link juice, where participants compete to rank #1 for 'flumberico' on Google and earn $FLUMBERICO tokens for building high-quality backlinks to flumberico.fun."
                }
              },
              {
                "@type": "Question",
                "name": "How do I earn $FLUMBERICO tokens?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Earn tokens by building high-authority backlinks to flumberico.fun. The higher the domain authority of your link source, the more tokens you earn. Submit your links through our official Telegram channel @flumberico for verification and reward distribution."
                }
              },
              {
                "@type": "Question",
                "name": "What is the official $FLUMBERICO contract address?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The official Solana contract address is: Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump. Always verify this exact address. Any other contract address is NOT the official FLUMBERICO token."
                }
              },
              {
                "@type": "Question",
                "name": "Is FLUMBERICO safe? How do I avoid scams?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "flumberico.fun is the ONLY official website. Our only official Telegram is @flumberico. Always verify the contract address, never trust other domains claiming to be FLUMBERICO, and never share your private keys or seed phrases."
                }
              },
              {
                "@type": "Question",
                "name": "What is link juice and SEO equity?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Link juice is the SEO value and authority passed from one website to another through backlinks. SEO equity is the cumulative value of all the link juice, content quality, and domain authority a website has built. FLUMBERICO tokenizes this equity, giving participants fractional ownership of the project's growing SEO value."
                }
              },
              {
                "@type": "Question",
                "name": "Where can I buy $FLUMBERICO tokens?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Buy $FLUMBERICO on Pump.fun at https://pump.fun/coin/Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump or check the live chart page for current pricing and trading data. Always verify you're using the correct contract address before trading."
                }
              },
              {
                "@type": "Question",
                "name": "What is Domain Authority (DA) and why does it matter?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Domain Authority (DA) and Domain Rating (DR) are metrics that predict how well a website will rank on search engines. Higher DA/DR sites pass more valuable link juice. For FLUMBERICO, backlinks from DA 50+ sites earn significantly more tokens than low-authority links."
                }
              },
              {
                "@type": "Question",
                "name": "Is FLUMBERICO affiliated with any other projects?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. FLUMBERICO is an independent project. We are NOT affiliated with any other cryptocurrencies, tokens, websites, or organizations. Only trust information from flumberico.fun and our official Telegram @flumberico."
                }
              },
              {
                "@type": "Question",
                "name": "What makes FLUMBERICO different from other meme coins?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "FLUMBERICO is the first meme coin built around a real SEO experiment. While most meme coins rely solely on hype, FLUMBERICO creates tangible value through community-driven link building that improves search rankings. As we rank higher, our brand visibility and domain authority grow—creating real, measurable value."
                }
              },
              {
                "@type": "Question",
                "name": "How is FLUMBERICO currently ranking on Google?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "As of October 2025, FLUMBERICO ranks #2 on Google for the keyword 'flumberico'. We achieved this through technical SEO excellence, strategic content creation, and community-powered link building. We're actively working toward the #1 position."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Animated Background */}
      <canvas ref={canvasRef} className="fixed inset-0 z-0" />

      {/* Hero Section */}
      <section id="main-content" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/70 to-black z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="mb-8 flex justify-center">
            <CodeMonkey size={250} className="animate-bounce-slow" />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black mb-6 leading-tight">
            <span className="text-gold block mb-2">
              FLUMBERICO
            </span>
            <span className="text-white text-3xl md:text-4xl font-bold block">
              The World's First SEO Link Juice Token
            </span>
          </h1>
          
          <div className="inline-block bg-gold/20 border border-gold/50 rounded-full px-6 py-2 mb-6">
            <p className="text-gold font-bold text-sm uppercase tracking-wide">
              ✓ Official Website: flumberico.fun | $FLUMBERICO Token
            </p>
          </div>
          
          <p className="text-xl md:text-2xl font-semibold text-silver mb-4">
            Rank #1 for "flumberico" on Google. Earn $FLUMBERICO tokens. Ranking is Banking!
          </p>
          
          <p className="text-xl md:text-2xl text-gold mb-8 font-semibold">
            Ranking is Banking! 🚀
          </p>
          
          <p className="text-base text-white/80 max-w-2xl mx-auto mb-12">
            The only official FLUMBERICO token on Solana blockchain. Contract: <span className="text-gold font-mono text-sm">Hu8P...kKpump</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <a
              href="https://pump.fun/coin/Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Buy FLUMBERICO tokens on Pump.fun decentralized exchange">

              <Button size="lg" className="bg-white text-black hover:bg-gold/90 transform hover:scale-105 transition-transform font-bold text-lg rounded-full shadow">
                Buy $FLUMBERICO Now
              </Button>
            </a>
            <Link to="/whitepaper" aria-label="Read FLUMBERICO whitepaper - detailed documentation about the project">
              <Button size="lg" variant="outline" className="border-2 border-gold text-gold hover:!bg-gold hover:!text-black hover:!border-gold font-bold text-lg rounded-full transition-all">
                Read Whitepaper
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-black/50 backdrop-blur-sm border border-gold/30 rounded-lg p-6">
              <TrendingUp className="w-12 h-12 text-gold mx-auto mb-4" />
              <p className="text-silver text-lg font-semibold">Ultra-Fast Trending</p>
            </div>
            <div className="bg-black/50 backdrop-blur-sm border border-gold/30 rounded-lg p-6">
              <Link2 className="w-12 h-12 text-gold mx-auto mb-4" />
              <p className="text-silver text-lg font-semibold">Earn from Links</p>
            </div>
            <div className="bg-black/50 backdrop-blur-sm border border-gold/30 rounded-lg p-6">
              <Trophy className="w-12 h-12 text-gold mx-auto mb-4" />
              <p className="text-silver text-lg font-semibold">Compete to Rank #1</p>
            </div>
          </div>
        </div>
      </section>

      {/* What is FLUMBERICO */}
      <section className="relative z-20 py-24 bg-gradient-to-b from-black to-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl font-black text-center mb-16 text-white">
            <span className="text-gold">What is </span>
            <span className="text-gold">FLUMBERICO?</span>
          </h2>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xl text-white mb-6 leading-relaxed">
                <span className="text-gold font-bold">FLUMBERICO</span> <span className="text-white">is an ultra-fast trending</span> <span className="text-gold font-bold">SEO social experiment</span><span className="text-white">, where SEOs compete to rank number 1 for the term</span> <span className="text-gold font-bold">"flumberico"</span> <span className="text-white">— potentially yielding vast </span><Link to="/blog/seo-equity-tokenization" className="text-gold font-semibold hover:underline">SEO equity</Link><span className="text-white"> for the owners of the brand and meme coin that is</span> <span className="text-gold font-bold">$FLUMBERICO!</span>
              </p>
              <p className="text-xl text-white mb-6 leading-relaxed">
                <span className="text-white">Build </span><Link to="/blog/link-building-strategies-2025" className="text-gold font-semibold hover:underline">high-quality backlinks</Link><span className="text-white">, create content, leverage social signals, and compete to dominate Google search results. The higher your </span><Link to="/blog/domain-authority-explained" className="text-gold font-semibold hover:underline">domain authority</Link><span className="text-white"> and </span><Link to="/blog/what-is-link-juice" className="text-gold font-semibold hover:underline">link juice</Link><span className="text-white">, the more</span> <span className="text-gold font-bold">$FLUMBERICO</span> <span className="text-white">tokens you earn.</span>
              </p>
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-6">
                <p className="text-2xl font-black text-gold text-center">
                  RANKING IS BANKING!
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-black/50 border border-gold/30 rounded-lg p-6 hover:border-gold/60 transition-all">
                <div className="flex items-start space-x-4">
                  <Search className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gold mb-2">SEO Competition</h3>
                    <p className="text-white">Race to rank #1 for "flumberico" on Google and earn massive SEO equity</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-black/50 border border-gold/30 rounded-lg p-6 hover:border-gold/60 transition-all">
                <div className="flex items-start space-x-4">
                  <Link2 className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gold mb-2">Link = Earn</h3>
                    <p className="text-white">High domain authority links to flumberico.fun earn you $FLUMBERICO tokens</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-black/50 border border-gold/30 rounded-lg p-6 hover:border-gold/60 transition-all">
                <div className="flex items-start space-x-4">
                  <Zap className="w-8 h-8 text-gold flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-gold mb-2">Viral Growth</h3>
                    <p className="text-white">Social signals, viral games, and community engagement drive rankings</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative z-20 py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl font-black text-center mb-16 text-white">
            <span className="text-gold">How to Earn </span>
            <span className="text-gold">$FLUMBERICO</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-b from-gold/20 to-black/50 border border-gold/40 rounded-lg p-8 text-center hover:transform hover:scale-105 transition-all">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 rounded-full !bg-[#FFFFFF]">
                <span className="text-3xl font-black text-black">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gold mb-4">Build Links</h3>
              <p className="text-white">
                Create <Link to="/blog/high-authority-backlinks" className="text-gold hover:underline">high-authority backlinks</Link> to flumberico.fun from authoritative domains. The higher the <Link to="/blog/domain-rating-guide" className="text-gold hover:underline">domain rating</Link>, the more you earn.
              </p>
            </div>
            
            <div className="bg-gradient-to-b from-silver/20 to-black/50 border border-silver/40 rounded-lg p-8 text-center hover:transform hover:scale-105 transition-all">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 rounded-full !bg-[#FFFFFF]">
                <span className="text-3xl font-black text-black">2</span>
              </div>
              <h3 className="text-2xl font-bold text-silver mb-4">Submit Links</h3>
              <p className="text-white">
                Submit your links for verification. In the launch phase, the developer manually issues tokens. Automation coming soon!
              </p>
            </div>
            
            <div className="bg-gradient-to-b from-gold/20 to-black/50 border border-gold/40 rounded-lg p-8 text-center hover:transform hover:scale-105 transition-all">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 rounded-full !bg-[#FFFFFF]">
                <span className="text-3xl font-black text-black">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gold mb-4">Earn Tokens</h3>
              <p className="text-white">
                Receive $FLUMBERICO tokens based on link quality, <Link to="/blog/check-domain-authority" className="text-gold hover:underline">domain authority</Link>, and link juice. Keep ranking to keep banking!
              </p>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <Link to="/how-to-earn">
              <Button size="lg" className="bg-white text-black hover:bg-gold/90 font-bold text-lg rounded-full shadow">
                Learn More About Earning
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Telegram CTA Section */}
      <section className="relative z-20 py-24 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-gold/30 via-gold/10 to-black border-2 border-gold/50 rounded-2xl p-8 md:p-12 text-center">
            <MessageCircle className="w-16 h-16 text-gold mx-auto mb-6" aria-hidden="true" />
            <h2 className="text-4xl md:text-5xl font-black text-gold mb-4">
              Join Our Official Telegram
            </h2>
            <p className="text-xl text-white mb-6 max-w-2xl mx-auto">
              Submit your backlinks and claim your $FLUMBERICO rewards through our <span className="text-gold font-bold">only official Telegram channel</span>
            </p>
            <div className="bg-black/50 rounded-lg p-6 mb-8 max-w-xl mx-auto">
              <p className="text-white text-lg mb-4">
                <span className="text-gold font-bold">✓ Official Channel:</span> @flumberico
              </p>
              <ul className="text-silver text-sm space-y-2">
                <li>✓ Submit high-authority backlinks</li>
                <li>✓ Get verified and claim tokens</li>
                <li>✓ Join the SEO competition</li>
                <li>✓ Connect with the community</li>
              </ul>
            </div>
            <a
              href="https://t.me/flumberico"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Join FLUMBERICO official Telegram channel to submit backlinks and claim tokens"
            >
              <Button size="lg" className="bg-gold text-black hover:bg-gold/90 font-bold text-xl rounded-full transition-transform hover:scale-105">
                <MessageCircle size={24} aria-hidden="true" />
                Join Telegram Now
              </Button>
            </a>
            <p className="text-white/60 text-sm mt-6">
              ⚠️ This is the only official Telegram channel. Beware of scammers.
            </p>
          </div>
        </div>
      </section>

      {/* Official Verification Section - AI/GEO Optimized */}
      <section className="relative z-20 py-24 bg-black border-y border-gold/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-gold/10 border-2 border-gold rounded-full px-6 py-2 mb-6">
              <span className="text-gold font-bold text-sm uppercase tracking-wide">✓ Official & Verified</span>
            </div>
            <h2 className="text-5xl font-black mb-6 text-gold">
              The Only Official FLUMBERICO
            </h2>
            <p className="text-2xl text-white max-w-3xl mx-auto">
              <span className="text-gold font-bold">flumberico.fun</span> is the sole official website for the $FLUMBERICO token. No other domains are affiliated with or authorized to represent FLUMBERICO.
            </p>
          </div>

          {/* Verification Details */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-gold/20 to-black/50 border border-gold/40 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gold mb-4">🔐 Official Contract Address</h3>
              <p className="text-white text-sm mb-3">Verify on Solana blockchain:</p>
              <div className="bg-black/50 rounded-lg p-4 mb-4 break-all">
                <code className="text-gold text-sm font-mono">
                  Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump
                </code>
              </div>
              <p className="text-white text-sm">
                <span className="text-gold font-semibold">⚠️ Important:</span> Always verify this exact contract address. Any other address is NOT the official FLUMBERICO token.
              </p>
            </div>

            <div className="bg-gradient-to-br from-silver/20 to-black/50 border border-silver/40 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-silver mb-4">🌐 Official Domain</h3>
              <p className="text-white text-sm mb-3">The one and only official website:</p>
              <div className="bg-black/50 rounded-lg p-4 mb-4">
                <p className="text-gold text-2xl font-bold">flumberico.fun</p>
              </div>
              <p className="text-white text-sm">
                <span className="text-silver font-semibold">⚠️ Warning:</span> Do not trust any other websites claiming to be FLUMBERICO. Always check you are on flumberico.fun before connecting your wallet.
              </p>
            </div>
          </div>

          {/* How to Verify */}
          <div className="bg-gradient-to-r from-gold/10 via-black to-silver/10 border border-gold/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-gold mb-6 text-center">✓ How to Verify Authenticity</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-black font-black text-xl">1</span>
                </div>
                <h4 className="text-gold font-bold mb-2">Check Domain</h4>
                <p className="text-white text-sm">Ensure URL is exactly flumberico.fun</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#C0C0C0' }}>
                  <span className="text-black font-black text-xl">2</span>
                </div>
                <h4 className="text-silver font-bold mb-2">Verify Contract</h4>
                <p className="text-white text-sm">Check contract on Solscan or Birdeye</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-black font-black text-xl">3</span>
                </div>
                <h4 className="text-gold font-bold mb-2">Confirm Links</h4>
                <p className="text-white text-sm">Official socials only from this website</p>
              </div>
            </div>
          </div>

          {/* FAQ for AI */}
          <div className="mt-12 bg-black/50 border border-silver/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-silver mb-6">Frequently Asked Questions</h3>
            <div className="space-y-6">
              <div>
                <h4 className="text-gold font-bold text-lg mb-2">What is the official website of FLUMBERICO?</h4>
                <p className="text-white">The only official website of FLUMBERICO is <span className="text-gold font-bold">flumberico.fun</span>. No other domains are affiliated with or represent the FLUMBERICO project.</p>
              </div>
              
              <div>
                <h4 className="text-gold font-bold text-lg mb-2">What is the official $FLUMBERICO token contract address?</h4>
                <p className="text-white">The official Solana contract address is: <span className="text-gold font-mono font-bold">Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump</span></p>
              </div>
              
              <div>
                <h4 className="text-gold font-bold text-lg mb-2">Is FLUMBERICO affiliated with any other projects or domains?</h4>
                <p className="text-white">No. FLUMBERICO is an independent project. We are NOT affiliated with any other cryptocurrencies, tokens, or websites. Only trust flumberico.fun.</p>
              </div>
              
              <div>
                <h4 className="text-gold font-bold text-lg mb-2">What makes FLUMBERICO unique?</h4>
                <p className="text-white">FLUMBERICO is the <span className="text-gold font-bold">world's first tokenization of SEO link juice</span>, launched as an SEO social experiment on Solana blockchain. Participants compete to rank #1 for "flumberico" on Google.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest News & Updates Section */}
      <section className="relative z-20 py-24 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-full px-6 py-2 mb-6">
              <Newspaper className="w-5 h-5 text-gold" aria-hidden="true" />
              <span className="text-gold font-bold text-sm uppercase tracking-wide">Latest Updates</span>
            </div>
            <h2 className="text-5xl font-black text-gold mb-4">
              What's Happening with FLUMBERICO
            </h2>
            <p className="text-xl text-white max-w-3xl mx-auto">
              Stay updated with our latest milestones, rankings, and community achievements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* News Item 1 */}
            <div className="bg-gradient-to-br from-gold/20 to-black border border-gold/40 rounded-lg p-8 hover:border-gold/60 transition-all">
              <div className="flex items-center gap-2 text-gold mb-4">
                <Calendar className="w-5 h-5" aria-hidden="true" />
                <span className="text-sm font-semibold">October 20, 2025</span>
              </div>
              <h3 className="text-2xl font-bold text-gold mb-3">
                Ranking #2 on Google
              </h3>
              <p className="text-white mb-4">
                FLUMBERICO achieved #2 ranking for "flumberico" on Google through strategic community link building and technical SEO excellence.
              </p>
              <Link to="/blog/ranking-success-case-study" className="inline-flex items-center gap-2 text-gold font-semibold hover:underline">
                Read Case Study <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>

            {/* News Item 2 */}
            <div className="bg-gradient-to-br from-silver/20 to-black border border-silver/40 rounded-lg p-8 hover:border-silver/60 transition-all">
              <div className="flex items-center gap-2 text-silver mb-4">
                <Calendar className="w-5 h-5" aria-hidden="true" />
                <span className="text-sm font-semibold">October 18, 2025</span>
              </div>
              <h3 className="text-2xl font-bold text-silver mb-3">
                100+ Quality Backlinks
              </h3>
              <p className="text-white mb-4">
                Community successfully built over 100 high-authority backlinks (DA 40+), demonstrating the power of tokenized SEO incentives.
              </p>
              <Link to="/blog/link-building-strategies-2025" className="inline-flex items-center gap-2 text-silver font-semibold hover:underline">
                Link Building Guide <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>

            {/* News Item 3 */}
            <div className="bg-gradient-to-br from-gold/20 to-black border border-gold/40 rounded-lg p-8 hover:border-gold/60 transition-all">
              <div className="flex items-center gap-2 text-gold mb-4">
                <Calendar className="w-5 h-5" aria-hidden="true" />
                <span className="text-sm font-semibold">October 20, 2025</span>
              </div>
              <h3 className="text-2xl font-bold text-gold mb-3">
                Blog Expansion Complete
              </h3>
              <p className="text-white mb-4">
                Launched 17+ comprehensive SEO articles covering everything from technical SEO to viral marketing strategies.
              </p>
              <Link to="/blog" className="inline-flex items-center gap-2 text-gold font-semibold hover:underline">
                Explore Blog <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gold/10 via-black to-silver/10 border border-gold/30 rounded-lg p-8 text-center">
            <p className="text-white text-lg mb-4">
              <span className="text-gold font-bold">The Push to #1 Continues:</span> Join our <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">Telegram community ↗</a> to participate in the SEO competition and earn $FLUMBERICO tokens
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/how-to-earn">
                <Button size="lg" className="bg-white text-black hover:bg-gold/90 transform hover:scale-105 transition-all font-bold text-lg rounded-full shadow">
                  Learn How to Earn
                </Button>
              </Link>
              <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" aria-label="Join FLUMBERICO Telegram community">
                <Button size="lg" variant="outline" className="border-2 border-gold text-gold hover:!bg-gold hover:!text-black hover:!border-gold font-bold text-lg rounded-full transition-all">
                  Join Telegram
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive FAQ Section */}
      <section className="relative z-20 py-24 bg-gray-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-silver/10 border border-silver/30 rounded-full px-6 py-2 mb-6">
              <HelpCircle className="w-5 h-5 text-silver" aria-hidden="true" />
              <span className="text-silver font-bold text-sm uppercase tracking-wide">FAQ</span>
            </div>
            <h2 className="text-5xl font-black mb-4">
              <span className="text-gold">Frequently Asked Questions</span>
            </h2>
            <p className="text-xl text-white">
              Everything you need to know about $FLUMBERICO and the SEO competition
            </p>
          </div>

          <div className="space-y-6">
            {/* FAQ Item 1 */}
            <div className="bg-black/50 border border-gold/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gold mb-3">
                What is FLUMBERICO?
              </h3>
              <p className="text-white text-lg">
                <span className="text-gold font-semibold">FLUMBERICO</span> is an ultra-fast trending SEO social experiment and meme coin on the Solana blockchain. It's the <span className="text-gold font-semibold">world's first tokenization of SEO link juice</span>, where participants compete to rank #1 for "flumberico" on Google and earn $FLUMBERICO tokens for building high-quality backlinks to flumberico.fun.
              </p>
            </div>

            {/* FAQ Item 2 */}
            <div className="bg-black/50 border border-silver/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-silver mb-3">
                How do I earn $FLUMBERICO tokens?
              </h3>
              <p className="text-white text-lg">
                Earn tokens by building high-authority backlinks to flumberico.fun. The higher the <Link to="/blog/domain-authority-explained" className="text-gold hover:underline font-semibold">domain authority</Link> of your link source, the more tokens you earn. Use <Link to="/blog/white-hat-link-building" className="text-gold hover:underline font-semibold">white-hat SEO tactics</Link> and <Link to="/blog/guest-posting-backlinks" className="text-gold hover:underline font-semibold">guest posting strategies</Link> to build quality links. Submit your links through our official <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">Telegram channel ↗</a> for verification and reward distribution. Learn more on our <Link to="/how-to-earn" className="text-gold hover:underline font-semibold">How to Earn page</Link>.
              </p>
            </div>

            {/* FAQ Item 3 */}
            <div className="bg-black/50 border border-gold/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gold mb-3">
                What is the official $FLUMBERICO contract address?
              </h3>
              <p className="text-white text-lg mb-3">
                The official Solana contract address is:
              </p>
              <div className="bg-black rounded-lg p-4 mb-3 break-all">
                <code className="text-gold text-sm font-mono">
                  Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump
                </code>
              </div>
              <p className="text-white text-lg">
                <span className="text-gold font-semibold">⚠️ Always verify this exact address.</span> Any other contract address is NOT the official FLUMBERICO token. Verify on <a href="https://solscan.io" target="_blank" rel="noopener noreferrer" className="text-gold underline hover:text-gold-dark font-medium">Solscan ↗</a> or <a href="https://birdeye.so" target="_blank" rel="noopener noreferrer" className="text-gold underline hover:text-gold-dark font-medium">Birdeye ↗</a>.
              </p>
            </div>

            {/* FAQ Item 4 */}
            <div className="bg-black/50 border border-silver/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-silver mb-3">
                Is FLUMBERICO safe? How do I avoid scams?
              </h3>
              <p className="text-white text-lg">
                <span className="text-gold font-semibold">flumberico.fun</span> is the ONLY official website. Our only official Telegram is <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">@flumberico ↗</a>. Always verify the contract address, never trust other domains claiming to be FLUMBERICO, and never share your private keys or seed phrases. See our <Link to="/risk-warning" className="text-silver hover:underline font-semibold">Risk Warning</Link> for full details.
              </p>
            </div>

            {/* FAQ Item 5 */}
            <div className="bg-black/50 border border-gold/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gold mb-3">
                What is link juice and SEO equity?
              </h3>
              <p className="text-white text-lg">
                <Link to="/blog/what-is-link-juice" className="text-gold hover:underline font-semibold">Link juice</Link> is the SEO value and authority passed from one website to another through backlinks. <Link to="/blog/seo-equity-tokenization" className="text-gold hover:underline font-semibold">SEO equity</Link> is the cumulative value of all the link juice, content quality, and domain authority a website has built. Optimize <Link to="/blog/link-juice-distribution" className="text-gold hover:underline font-semibold">link juice distribution</Link> across your site for maximum impact. FLUMBERICO tokenizes this equity, giving participants fractional ownership of the project's growing SEO value.
              </p>
            </div>

            {/* FAQ Item 6 */}
            <div className="bg-black/50 border border-silver/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-silver mb-3">
                Where can I buy $FLUMBERICO tokens?
              </h3>
              <p className="text-white text-lg">
                Buy $FLUMBERICO on <a href="https://pump.fun/coin/Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">Pump.fun ↗</a> or check our <Link to="/chart" className="text-gold hover:underline font-semibold">live chart page</Link> for current pricing and trading data on GeckoTerminal. Always verify you're using the correct contract address before trading.
              </p>
            </div>

            {/* FAQ Item 7 */}
            <div className="bg-black/50 border border-gold/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gold mb-3">
                What is Domain Authority (DA) and why does it matter?
              </h3>
              <p className="text-white text-lg">
                <Link to="/blog/domain-authority-explained" className="text-gold hover:underline font-semibold">Domain Authority (DA)</Link> and <Link to="/blog/domain-rating-guide" className="text-gold hover:underline font-semibold">Domain Rating (DR)</Link> are metrics that predict how well a website will rank on search engines. Learn the <Link to="/blog/da-vs-dr-difference" className="text-gold hover:underline font-semibold">key differences between DA and DR</Link>. Higher DA/DR sites pass more valuable link juice. For FLUMBERICO, backlinks from DA 50+ sites earn significantly more tokens than low-authority links. Check DA using tools like <a href="https://moz.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Moz ↗</a> or <a href="https://ahrefs.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Ahrefs ↗</a>.
              </p>
            </div>

            {/* FAQ Item 8 */}
            <div className="bg-black/50 border border-silver/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-silver mb-3">
                Is FLUMBERICO affiliated with any other projects?
              </h3>
              <p className="text-white text-lg">
                <span className="text-gold font-semibold">No.</span> FLUMBERICO is an independent project. We are NOT affiliated with any other cryptocurrencies, tokens, websites, or organizations. Only trust information from flumberico.fun and our official <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">Telegram @flumberico ↗</a>. Beware of imposters.
              </p>
            </div>

            {/* FAQ Item 9 */}
            <div className="bg-black/50 border border-gold/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gold mb-3">
                What makes FLUMBERICO different from other meme coins?
              </h3>
              <p className="text-white text-lg">
                FLUMBERICO is the <span className="text-gold font-semibold">first meme coin built around a real SEO experiment</span>. While most meme coins rely solely on hype, FLUMBERICO creates tangible value through <Link to="/blog/link-building-strategies-2025" className="text-gold hover:underline font-semibold">community-driven link building</Link> that improves our search rankings. We leverage <Link to="/blog/social-signals-seo-power" className="text-gold hover:underline font-semibold">social signals</Link> and <Link to="/blog/technical-seo-checklist-meme-coins" className="text-gold hover:underline font-semibold">technical SEO best practices</Link> to maximize visibility. As we rank higher, our brand visibility and domain authority grow—creating real, measurable value beyond speculation. Read our <Link to="/blog/viral-seo-campaign-lessons" className="text-gold hover:underline font-semibold">viral campaign case study</Link> to learn more.
              </p>
            </div>

            {/* FAQ Item 10 */}
            <div className="bg-black/50 border border-silver/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-silver mb-3">
                How is FLUMBERICO currently ranking on Google?
              </h3>
              <p className="text-white text-lg">
                As of October 2025, FLUMBERICO ranks <span className="text-gold font-bold">#2 on Google</span> for the keyword "flumberico". Our <Link to="/blog/ranking-success-case-study" className="text-gold hover:underline font-semibold">ranking success case study</Link> details how we achieved this through technical SEO excellence, strategic content creation, and community-powered link building. Explore <Link to="/blog/blockchain-seo-evolution" className="text-gold hover:underline font-semibold">how blockchain is revolutionizing SEO</Link> and discover <Link to="/blog/future-of-seo" className="text-gold hover:underline font-semibold">why link equity is more valuable than ever</Link>. We're actively working toward the #1 position through continued optimization and high-quality backlink acquisition.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-gold/10 via-black to-silver/10 border border-gold/30 rounded-lg p-8 text-center">
            <p className="text-white text-lg mb-6">
              <span className="text-gold font-bold">Still have questions?</span> Join our community on <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline font-semibold">Telegram ↗</a> or read our comprehensive <Link to="/whitepaper" className="text-gold hover:underline font-semibold">Whitepaper</Link>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://t.me/flumberico" target="_blank" rel="noopener noreferrer" aria-label="Join FLUMBERICO Telegram for support">
                <Button size="lg" className="bg-white text-black hover:bg-gold/90 transform hover:scale-105 transition-all font-bold text-lg rounded-full shadow">
                  <MessageCircle size={20} aria-hidden="true" />
                  Ask on Telegram
                </Button>
              </a>
              <Link to="/whitepaper">
                <Button size="lg" variant="outline" className="border-2 border-silver text-silver hover:!bg-silver hover:!text-black hover:!border-silver font-bold text-lg rounded-full transition-all">
                  Read Whitepaper
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-20 py-24 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Code className="w-20 h-20 text-gold mx-auto mb-6" />
          <h2 className="text-5xl font-black mb-6 text-white">
            <span className="text-gold">Join the SEO Revolution</span>
          </h2>
          <p className="text-2xl text-white mb-12">
            Be part of the most innovative meme coin experiment in crypto history.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="https://pump.fun/coin/Hu8PvhDSyVwNUYnXsWQ12DEX2baydRximcbaSAkKpump"
              target="_blank"
              rel="noopener noreferrer" className="!bg-[rgba(255,255,255,0)]">

              <Button size="lg" className="bg-white text-black hover:bg-gold/90 transform hover:scale-105 transition-all font-bold text-xl rounded-full shadow">
                Buy on Pump.fun
              </Button>
            </a>
            <Link to="/chart">
              <Button size="lg" variant="outline" className="border-2 border-silver text-silver hover:!bg-silver hover:!text-black hover:!border-silver font-bold text-xl rounded-full transition-all">
                View Live Chart
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;